import mixpanel from 'mixpanel-browser';

import { AnalyticsEventName, TrackEventArgs } from './analytics-events';
import { APP_VERSION, getConfig, isDevelopment } from './config';

const EU_API_HOST = 'https://api-eu.mixpanel.com';

let isInitialized = false;

export function initMixpanel(): void {
  if (isInitialized || typeof window === 'undefined') return;

  const { mixpanelToken } = getConfig();
  if (!mixpanelToken) {
    if (isDevelopment) {
      console.warn('[mixpanel] no token configured, analytics disabled');
    }
    return;
  }

  mixpanel.init(mixpanelToken, {
    api_host: EU_API_HOST,
    track_pageview: false,
    persistence: 'localStorage',
    debug: isDevelopment,
  });

  isInitialized = true;

  // Attach the running app version to every subsequent event so we can slice
  // analytics dashboards by release and correlate behaviour changes with
  // specific deploys. Matches the `appVersion` super property documented in
  // `docs/tracing-plan.md`.
  mixpanel.register({ appVersion: APP_VERSION });
}

export function identifyMixpanelUser(
  userId: string,
  properties?: Record<string, unknown>
): void {
  if (!isInitialized) return;

  mixpanel.identify(userId);

  if (properties) {
    const cleaned = Object.fromEntries(
      Object.entries(properties).filter(
        ([, value]) => value !== undefined && value !== null
      )
    );
    mixpanel.people.set(cleaned);
  }
}

export function registerSuperProperties(
  properties: Record<string, unknown>
): void {
  if (!isInitialized) return;
  mixpanel.register(properties);
}

/**
 * Associates the current user with a Mixpanel group (Group Analytics) so
 * metrics can be aggregated by an entity other than the user — e.g. per brand
 * via `brandId`. `set_group` registers the key as a super property on every
 * subsequent event and links the user to the group profile.
 *
 * Requirements & caveats:
 * - The group key must also be declared in Mixpanel Project Settings → Group
 *   Keys (config, not code).
 * - Attribution is **not** retroactive: only events sent after the key exists
 *   (and after this call) are grouped.
 * See `docs/tracing-plan.md`.
 */
export function setMixpanelGroup(groupKey: string, groupId: string): void {
  if (!isInitialized) return;
  mixpanel.set_group(groupKey, groupId);
}

/**
 * Sets properties on a Group Profile (the entity behind a group key, e.g. a
 * brand or an offer). Useful for slowly-changing descriptive attributes
 * (brand tiering, legal status, operation type): the profile keeps the latest
 * value and reports grouped by that entity reflect it.
 *
 * `null`/`undefined` values are dropped (no-op if nothing remains), so callers
 * can pass optional GraphQL fields directly.
 */
export function setMixpanelGroupProfile(
  groupKey: string,
  groupId: string,
  properties: Record<string, unknown>
): void {
  if (!isInitialized) return;
  const cleaned = Object.fromEntries(
    Object.entries(properties).filter(
      ([, value]) => value !== undefined && value !== null
    )
  );
  if (Object.keys(cleaned).length === 0) return;
  mixpanel.get_group(groupKey, groupId).set(cleaned);
}

// Filet de sécurité : un même couple (nom, propriétés) émis dans cette
// fenêtre est ignoré. Couvre les replays React StrictMode, les
// republications de subscription Apollo, les reconnexions WS, etc., sans
// dépendre du guard de chaque call site.
const TRACK_EVENT_DEDUP_WINDOW_MS = 2_000;
const recentEvents = new Map<string, number>();

function shouldDropAsDuplicate(key: string, now: number): boolean {
  const lastSeen = recentEvents.get(key);
  if (lastSeen !== undefined && now - lastSeen < TRACK_EVENT_DEDUP_WINDOW_MS) {
    return true;
  }
  recentEvents.set(key, now);
  // Évite la croissance unbounded : on purge les entrées expirées dès que
  // la map dépasse un seuil raisonnable.
  if (recentEvents.size > 200) {
    for (const [k, t] of recentEvents) {
      if (now - t >= TRACK_EVENT_DEDUP_WINDOW_MS) recentEvents.delete(k);
    }
  }
  return false;
}

export function trackEvent<K extends AnalyticsEventName>(
  ...args: TrackEventArgs<K>
): void {
  if (!isInitialized) return;
  const [eventName, eventProperties] = args;
  const key = `${eventName}|${JSON.stringify(eventProperties ?? {})}`;
  if (shouldDropAsDuplicate(key, Date.now())) return;
  mixpanel.track(eventName, eventProperties);
}

export function resetMixpanel(): void {
  if (!isInitialized) return;
  mixpanel.reset();
}
